import React, { ReactElement, useEffect, useMemo, useState } from 'react';

import history from 'configs/history';
import { getUrlParams, setUrlParams } from 'utils/urlParams';

import { TTabKey, TTabProps } from './Tab.types';
import Header from './components/Header';
import Content from './components/Content';
import styles from './Tab.module.scss';

function Tab({
  tabs,
  selectedTab,
  onTabChange,
  beforeContent,
  afterContent,
  gutter = false,
}: TTabProps): ReactElement {
  const urlTabParam = getUrlParams().get('tab');

  const [selectedTabKey, setSelectedTabKey] = useState<TTabKey>(
    selectedTab || urlTabParam || tabs[0].key
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tabRefs: React.RefObject<any>[] = useMemo(
    () =>
      Array(tabs.length)
        .fill(null)
        .map(() => React.createRef()),
    []
  );

  const currentTabIndex = useMemo(
    () => tabs.findIndex((t) => t.key === selectedTabKey),
    [selectedTabKey]
  );
  useEffect(() => {
    const selectedElement = document.getElementById(`tab-${selectedTabKey}`);
    const outerElement = document.querySelector('[data-testid="TabHeader"]');
    if (selectedElement && outerElement) {
      outerElement.scrollLeft =
        selectedElement.getBoundingClientRect().x -
        outerElement.getBoundingClientRect().width / 2;
    }
  }, [selectedTabKey]);

  useEffect(() => {
    !urlTabParam &&
      history.replace({
        pathname: window.location.pathname,
        search: setUrlParams({ tab: selectedTabKey }),
      });
    if (urlTabParam) {
      const tabFromUrl = tabs.find((tab) => tab.key === urlTabParam);
      if (tabFromUrl) {
        setSelectedTabKey(tabFromUrl.key);
      }
    }
  }, []);

  useEffect(() => {
    if (selectedTab) {
      setSelectedTabKey(selectedTab);
    }
  }, [selectedTab]);

  function getCurrentTabContent() {
    return tabs[currentTabIndex].component;
  }

  function handleTabChange(tabKey: TTabKey) {
    onTabChange?.(tabKey);
    setSelectedTabKey(tabKey);
    history.replace({
      pathname: `/${tabKey}`,
      search: setUrlParams({ tab: tabKey }),
    });
  }

  return (
    <div className={styles.tabsWrapper} data-testid="Tab">
      {tabs.length > 1 && (
        <Header
          tabRefs={tabRefs}
          selectedTab={{ key: selectedTabKey, index: currentTabIndex }}
          tabs={tabs}
          onTabChange={handleTabChange}
        />
      )}
      <Content
        beforeContent={beforeContent}
        gutter={gutter}
        afterContent={afterContent}
        tabContent={getCurrentTabContent()}
      />
    </div>
  );
}

export default Tab;
